import { Button, OTP_VALIDITY_SECONDS } from '@cibli/shared';
import { ArrowLeft, Mail, MessageSquare } from 'lucide-react';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useOTPVerification, useTimer, OtpInput } from '@/features/auth/otp';
import IconButton from '@/features/common/components/IconButton';
import MainLayout from '@/features/common/components/MainLayout';
import { formatPhoneForDisplay } from '@/lib/utils/phone';

import type { OtpState } from '../hooks/useKioskOtp';

import EmailInputModal from '../components/EmailInputModal';
import PhoneInputModal from '../components/PhoneInputModal';
import SmsErrorModal from '../components/SmsErrorModal';
import {
  EMAIL_FALLBACK_DELAY_SECONDS,
  EMAIL_RESEND_COOLDOWN_SECONDS,
  SMS_FALLBACK_DELAY_SECONDS,
  SMS_RESEND_COOLDOWN_SECONDS,
} from '../hooks/useKioskOtp';

type KioskOtpScreenProps = {
  otpContext: OtpState;
  onVerified: () => Promise<void>;
  onResend: () => Promise<void>;
  onEmailFallback: (email: string) => Promise<void>;
  onSmsFallback: (phone?: string) => Promise<void>;
  onClearSmsError: () => void;
  onBack: () => void;
  onCancel: () => void;
};

const KioskOtpScreen = memo(function KioskOtpScreen({
  otpContext,
  onVerified,
  onResend,
  onEmailFallback,
  onSmsFallback,
  onClearSmsError,
  onBack,
  onCancel,
}: KioskOtpScreenProps) {
  const { t } = useTranslation();
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [showSmsErrorModal, setShowSmsErrorModal] = useState(false);
  const [isSendingFallback, setIsSendingFallback] = useState(false);

  const isEmail = otpContext.mode === 'email';
  // Cooldowns depend on the booth OTP mode (EMAIL: resend 5s / SMS fallback 45s,
  // SMS: resend 30s / email fallback 30s)
  const resendCooldown = isEmail ? EMAIL_RESEND_COOLDOWN_SECONDS : SMS_RESEND_COOLDOWN_SECONDS;
  const fallbackDelay = isEmail ? SMS_FALLBACK_DELAY_SECONDS : EMAIL_FALLBACK_DELAY_SECONDS;

  const [fallbackCountdown, setFallbackCountdown] = useState(fallbackDelay);

  const timer = useTimer({
    initialSeconds: Math.min(
      Math.max(otpContext.timeLeft ?? OTP_VALIDITY_SECONDS, 1),
      OTP_VALIDITY_SECONDS,
    ),
  });

  // Show SMS error modal when SMS fails
  useEffect(() => {
    if (otpContext.mode === 'sms' && otpContext.smsError) {
      setShowSmsErrorModal(true);
    }
  }, [otpContext]);

  // Countdown for the fallback channel button
  useEffect(() => {
    if (fallbackCountdown <= 0) return;

    const interval = setInterval(() => {
      setFallbackCountdown(prev => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(interval);
  }, [fallbackCountdown]);

  // Restart the fallback lock whenever the active channel changes
  useEffect(() => {
    setFallbackCountdown(fallbackDelay);
  }, [fallbackDelay]);

  // Determine if we're using email or SMS
  const identifier = isEmail ? otpContext.emailFallback : otpContext.phoneNumber;

  const verification = useOTPVerification({
    identifier,
    isEmail,
    onSuccess: onVerified,
    onResend: async () => {
      try {
        await onResend();
        timer.reset(OTP_VALIDITY_SECONDS);
      } catch {
        // Error already shown via toast in useKioskOtp
      }
    },
  });

  const formattedDisplay = useMemo(() => {
    if (otpContext.mode === 'email') {
      return otpContext.emailFallback;
    }
    return formatPhoneForDisplay(otpContext.displayPhone || otpContext.phoneNumber);
  }, [otpContext]);

  const resendCountdown = useMemo(
    () => Math.max(0, resendCooldown - Math.max(0, OTP_VALIDITY_SECONDS - timer.timeLeft)),
    [timer.timeLeft, resendCooldown],
  );

  const fallbackLabelKey = isEmail ? 'KioskOTP.smsButton' : 'KioskOTP.emailButton';
  const fallbackTimerKey = isEmail ? 'KioskOTP.smsButtonTimer' : 'KioskOTP.emailButtonTimer';

  const handleFallbackClick = useCallback(() => {
    if (isSendingFallback) return;

    if (otpContext.mode === 'email') {
      // SMS fallback: use the phone saved earlier, or ask for it
      if (otpContext.phoneNumber) {
        setIsSendingFallback(true);
        onSmsFallback(otpContext.phoneNumber).finally(() => setIsSendingFallback(false));
      } else {
        setShowPhoneModal(true);
      }
      return;
    }

    const userEmail = otpContext.mode === 'sms' ? otpContext.userEmail : undefined;
    if (userEmail) {
      // User has email, send directly
      setIsSendingFallback(true);
      onEmailFallback(userEmail).finally(() => setIsSendingFallback(false));
    } else {
      // User doesn't have email, show modal
      setShowEmailModal(true);
    }
  }, [isSendingFallback, otpContext, onEmailFallback, onSmsFallback]);

  const handleSmsErrorModalSendByEmail = useCallback(() => {
    setShowSmsErrorModal(false);
    onClearSmsError();
    handleFallbackClick();
  }, [onClearSmsError, handleFallbackClick]);

  const handleSmsErrorModalClose = useCallback(() => {
    setShowSmsErrorModal(false);
    onClearSmsError();
  }, [onClearSmsError]);

  const handleEmailModalSubmit = useCallback(
    async (email: string) => {
      setIsSendingFallback(true);
      try {
        await onEmailFallback(email);
        setShowEmailModal(false);
      } catch {
        // Error already shown via toast in useKioskOtp
      } finally {
        setIsSendingFallback(false);
      }
    },
    [onEmailFallback],
  );

  const handlePhoneModalSubmit = useCallback(
    async (phone: string) => {
      setIsSendingFallback(true);
      try {
        await onSmsFallback(phone);
        setShowPhoneModal(false);
      } catch {
        // Error already shown via toast in useKioskOtp
      } finally {
        setIsSendingFallback(false);
      }
    },
    [onSmsFallback],
  );

  return (
    <MainLayout
      className="gap-8 bg-muted/40 text-center"
      onClose={onCancel}
      rightContent={
        <IconButton
          className="ml-6"
          icon={<ArrowLeft strokeWidth={3} className="min-h-6 min-w-6" aria-hidden="true" />}
          onClick={onBack}
          aria-label={t('common.back')}
        />
      }
    >
      <p className="text-sm font-semibold uppercase tracking-[0.25em] text-primary">
        {t('KioskOTP.chime')}
      </p>
      <h1 className="text-3xl font-semibold md:text-4xl text-balance">
        {t(isEmail ? 'KioskOTP.titleEmail' : 'KioskOTP.title')}
      </h1>

      {/* Show email fallback info */}
      {isEmail && (
        <div
          role="status"
          className="rounded-lg bg-blue-50 border border-blue-200 p-4 text-sm text-blue-800"
        >
          <p className="font-medium">{t('KioskOTP.emailSent')}</p>
          <p className="mt-1">{formattedDisplay}</p>
        </div>
      )}

      <p className="mx-auto max-w-2xl text-lg text-muted-foreground text-balance">
        {t(
          isEmail
            ? otpContext.hasExistingOtp
              ? 'KioskOTP.subtitleEmailExisting'
              : 'KioskOTP.subtitleEmail'
            : otpContext.hasExistingOtp
              ? 'KioskOTP.subtitleExisting'
              : 'KioskOTP.subtitle',
          { phone: formattedDisplay, email: formattedDisplay },
        )}
      </p>

      <div className="flex w-full max-w-2xl flex-col items-center gap-7 p-2 sm:p-0">
        <OtpInput
          value={verification.code}
          onChange={verification.handleChange}
          disabled={verification.isSubmitting}
          aria-label={t('KioskOTP.otpInputLabel')}
        />
        {verification.error ? (
          <p id="otp-error" role="alert" className="text-sm text-destructive">
            {verification.error}
          </p>
        ) : null}

        <div className="flex w-full justify-center">
          <Button
            variant="secondary"
            size="lg"
            className="w-full sm:w-auto min-w-[230px]"
            onClick={verification.handleResend}
            disabled={verification.isSubmitting || resendCountdown > 0}
          >
            {resendCountdown > 0
              ? t('KioskOTP.resendTimer', { seconds: resendCountdown })
              : t('KioskOTP.resend')}
          </Button>
        </div>

        {/* Fallback channel: SMS when the code went out by email, email otherwise */}
        <Button
          variant="outline"
          size="lg"
          className="w-full sm:w-auto min-w-[230px]"
          onClick={handleFallbackClick}
          disabled={fallbackCountdown > 0 || isSendingFallback}
          aria-label={
            fallbackCountdown > 0
              ? t(fallbackTimerKey, { seconds: fallbackCountdown })
              : t(fallbackLabelKey)
          }
        >
          {isEmail ? (
            <MessageSquare className="mr-2 h-4 w-4" aria-hidden="true" />
          ) : (
            <Mail className="mr-2 h-4 w-4" aria-hidden="true" />
          )}
          <span aria-live="polite" aria-atomic="true">
            {fallbackCountdown > 0
              ? t(fallbackTimerKey, { seconds: fallbackCountdown })
              : t(fallbackLabelKey)}
          </span>
        </Button>

        <Button
          size="lg"
          className="w-full sm:w-auto"
          onClick={verification.handleManualSubmit}
          disabled={verification.isSubmitting || verification.code.replace(/\D/g, '').length !== 6}
        >
          {verification.isSubmitting ? t('KioskOTP.verifying') : t('KioskOTP.submit')}
        </Button>
      </div>

      {/* SMS Error Modal */}
      <SmsErrorModal
        open={showSmsErrorModal}
        onOpenChange={handleSmsErrorModalClose}
        onSendByEmail={handleSmsErrorModalSendByEmail}
        isLoading={isSendingFallback}
      />

      {/* Email input modal */}
      <EmailInputModal
        open={showEmailModal}
        onOpenChange={setShowEmailModal}
        onSubmit={handleEmailModalSubmit}
        isLoading={isSendingFallback}
      />

      {/* Phone input modal (SMS fallback when the phone was skipped) */}
      <PhoneInputModal
        open={showPhoneModal}
        onOpenChange={setShowPhoneModal}
        onSubmit={handlePhoneModalSubmit}
        isLoading={isSendingFallback}
      />
    </MainLayout>
  );
});

export default KioskOtpScreen;
